import React, { useState } from 'react';
import { CampaignCreate } from '../../types/campaign.types';
import Input from '../common/Input';
import Button from '../common/Button';
import {
    validateCampaignName,
    validateSenderId,
    validateMessageTemplate,
} from '../../utils/validators';

interface CampaignFormProps {
    onSubmit: (data: CampaignCreate) => Promise<void>;
    onCancel: () => void;
    loading?: boolean;
}

const CampaignForm: React.FC<CampaignFormProps> = ({
    onSubmit,
    onCancel,
    loading = false,
}) => {
    const [formData, setFormData] = useState<CampaignCreate>({
        name: '',
        description: '',
        message_template: '',
        sender_id: '',
        client_name: '',
        client_email: '',
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        const nameValidation = validateCampaignName(formData.name);
        if (!nameValidation.valid) {
            newErrors.name = nameValidation.error!;
        }

        const senderIdValidation = validateSenderId(formData.sender_id);
        if (!senderIdValidation.valid) {
            newErrors.sender_id = senderIdValidation.error!;
        }

        const messageValidation = validateMessageTemplate(formData.message_template);
        if (!messageValidation.valid) {
            newErrors.message_template = messageValidation.error!;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        await onSubmit({
            ...formData,
            client_name: formData.client_name?.trim() || undefined,
            client_email: formData.client_email?.trim() || undefined,
        });
    };

    const messageLength = formData.message_template.length;
    const smsCount = Math.ceil(messageLength / 160);

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <Input
                id="name"
                name="name"
                label="Campaign Name"
                value={formData.name}
                onChange={handleChange}
                error={errors.name}
                placeholder="e.g., New Year Promotion"
                required
            />

            <div>
                <label
                    htmlFor="description"
                    className="block text-sm font-medium text-gray-700 mb-1"
                >
                    Description
                </label>
                <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Brief description of the campaign"
                />
            </div>

            <Input
                id="sender_id"
                name="sender_id"
                label="Sender ID"
                value={formData.sender_id}
                onChange={handleChange}
                error={errors.sender_id}
                placeholder="e.g., YourBrand"
                helpText="Max 11 alphanumeric characters"
                maxLength={11}
                required
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input
                    id="client_name"
                    name="client_name"
                    label="Client Name"
                    value={formData.client_name || ''}
                    onChange={handleChange}
                    placeholder="e.g., Acme Ltd"
                    helpText="Appears on the payment receipt"
                />
                <Input
                    id="client_email"
                    name="client_email"
                    label="Client Email"
                    type="email"
                    value={formData.client_email || ''}
                    onChange={handleChange}
                    placeholder="billing@example.com"
                    helpText="Used when sending the receipt"
                />
            </div>

            <div>
                <label
                    htmlFor="message_template"
                    className="block text-sm font-medium text-gray-700 mb-1"
                >
                    Message Template
                    <span className="text-red-500 ml-1">*</span>
                </label>
                <textarea
                    id="message_template"
                    name="message_template"
                    value={formData.message_template}
                    onChange={handleChange}
                    rows={4}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Hello {name}, this is your message..."
                    maxLength={480}
                />
                <div className="mt-1 flex justify-between text-xs text-gray-500">
                    <span>
                        Use {'{name}'} for personalization
                    </span>
                    <span>
                        {messageLength}/480 characters ({smsCount} SMS)
                    </span>
                </div>
                {errors.message_template && (
                    <p className="mt-1 text-sm text-red-600">{errors.message_template}</p>
                )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <h4 className="text-sm font-medium text-blue-900 mb-2">
                    Message Preview
                </h4>
                <p className="text-sm text-blue-800 whitespace-pre-wrap">
                    {formData.message_template.replace(
                        /{name}/g,
                        '<span class="font-semibold">John Doe</span>'
                    ) || 'Your message will appear here...'}
                </p>
            </div>

            <div className="flex gap-3 pt-4">
                <Button type="submit" variant="primary" loading={loading} fullWidth>
                    Create Campaign
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    onClick={onCancel}
                    disabled={loading}
                >
                    Cancel
                </Button>
            </div>
        </form>
    );
};

export default CampaignForm;
